import React, { useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import { 
  initializeAnalytics,
  trackApplicationHome,
  trackCitizenshipStep,
  trackTuitionStep,
  trackUploadDocumentPage,
  trackPageView,
  ANALYTICS_CONFIG,
  getFullPageName,
  trackCustomPageView,
} from '../../services/analytics';
import { safeGet } from '../../utils/safeGet';

interface AnalyticsRouteProps {
  children: React.ReactNode;
  pageType: 
    | 'citizenship-home'
    | 'citizenship-apply'
    | 'citizenship-upload'
    | 'citizenship-manager-portal'
    | 'citizenship-manager-details'
    | 'tuition-home'
    | 'tuition-apply'
    | 'tuition-upload'
    | 'tuition-manager-portal'
    | 'tuition-manager-details'
    | 'tuition-overview'
    | 'admin-citizenship'
    | 'admin-tuition';
  currentStep?: number;
  contentTitle?: string;
}

// Track if analytics has been initialized (to avoid re-initializing)
let analyticsInitialized = false;

/**
 * HOC that wraps route components and handles ALL analytics
 * - Initializes analytics once on first route
 * - Tracks page views for each route
 * - Gets user data from Redux automatically
 */
const AnalyticsRoute: React.FC<AnalyticsRouteProps> = ({
  children,
  pageType,
  currentStep,
  contentTitle,
}) => {
  // Get user data from Redux
  const authData = useAppSelector((state: any) => safeGet(state?.auth, {}));
  const userProfile = useAppSelector((state: any) => safeGet(state?.user, {}));
  const profileData = useAppSelector((state: any) => safeGet(state?.profile, {}));

  // Merge user data from multiple possible Redux slices
  const userData = {
    nuid: safeGet(sessionStorage.getItem('userId'), safeGet(userProfile?.nuid, safeGet(profileData?.nuid, ''))),
    email: safeGet(authData?.email, safeGet(userProfile?.email, safeGet(profileData?.email, ''))),
    region: safeGet(authData?.region, safeGet(userProfile?.region, safeGet(profileData?.region, ''))),
    userType: safeGet(authData?.userType, safeGet(userProfile?.userType, safeGet(profileData?.userType, ''))),
  };

  // Initialize analytics ONCE on first mount (when we have user data)
  useEffect(() => {
    if (!analyticsInitialized && userData.nuid) {
      try {
        console.log('AnalyticsRoute: Initializing analytics (first route load)...');
        initializeAnalytics(userData);
        analyticsInitialized = true;
        console.log('Analytics initialized via AnalyticsRoute');
      } catch (error) {
        console.error('Error initializing analytics:', error);
      }
    }
  }, [userData.nuid]); // Only initialize when nuid becomes available

  // Track page view whenever pageType or step changes
  useEffect(() => {
    try {
      const stepInfo = currentStep ? ` (step ${currentStep})` : '';
      console.log(`📊 AnalyticsRoute: Tracking page type: ${pageType}${stepInfo}`);

      switch (pageType) {
        // ==================== CITIZENSHIP ROUTES ====================
        case 'citizenship-home':
          trackApplicationHome('citizenship');
          break;

        case 'citizenship-apply':
          if (currentStep && currentStep >= 1 && currentStep <= 4) {
            trackCitizenshipStep(currentStep as 1 | 2 | 3 | 4);
          } else {
            trackCitizenshipStep(1);
          }
          break;

        case 'citizenship-upload':
          trackUploadDocumentPage('citizenship', 'summary');
          break;

        case 'citizenship-manager-portal':
          trackPageView(ANALYTICS_CONFIG.CITIZENSHIP_PAGES.MANAGER_PORTAL, {
            contentTitle: safeGet(contentTitle, 'Citizenship Manager Portal'),
            contentType: 'manager-portal',
          });
          break;

        case 'citizenship-manager-details':
          trackPageView(ANALYTICS_CONFIG.CITIZENSHIP_PAGES.MANAGER_APPLICATION_TAB, {
            contentTitle: safeGet(contentTitle, 'Citizenship Manager Details'),
            contentType: 'manager-details',
          });
          break;

        // ==================== TUITION ROUTES ====================
        case 'tuition-home':
          trackApplicationHome('tuition');
          break;

        case 'tuition-apply':
          if (currentStep && currentStep >= 1 && currentStep <= 8) {
            trackTuitionStep(currentStep as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8);
          } else {
            trackTuitionStep(1);
          }
          break;

        case 'tuition-upload':
          trackUploadDocumentPage('tuition', 'summary');
          trackCustomPageView('status-documents');
          break;

        case 'tuition-manager-portal':
          trackPageView(ANALYTICS_CONFIG.TUITION_PAGES.MANAGER_PORTAL, {
            contentTitle: safeGet(contentTitle, 'Tuition Manager Portal'),
            contentType: 'manager-portal',
          });
          trackCustomPageView('manager-portal');
          break;

        case 'tuition-manager-details':
          trackPageView(ANALYTICS_CONFIG.TUITION_PAGES.MANAGER_VIEW_APPLICANT, {
            contentTitle: safeGet(contentTitle, 'Tuition Manager Details'),
            contentType: 'manager-details',
          });
          trackCustomPageView('manager-portal:view-details:applicant')
          break;

        case 'tuition-overview':
          trackPageView(ANALYTICS_CONFIG.TUITION_PAGES.EMPLOYEE_INFO_TAB, {
            contentTitle: safeGet(contentTitle, 'Tuition Employee Overview'),
            contentType: 'overview',
          });
          trackCustomPageView('my-employee-info:employee-info-tab');
          break;

        // ==================== ADMIN ROUTES ====================
        case 'admin-citizenship':
          trackPageView(getFullPageName('admin:citizenship'), {
            contentTitle: 'Citizenship Admin',
            contentType: 'admin',
          });
          break;

        case 'admin-tuition':
          trackPageView(getFullPageName('admin:tuition'), {
            contentTitle: 'Tuition Admin',
            contentType: 'admin',
          });
          break;

        default:
          console.warn(`⚠️ Unknown page type: ${pageType}`);
      }
    } catch (error) {
      console.error(`❌ Error tracking page ${pageType}:`, error);
    }
  }, [pageType, currentStep, contentTitle]);

  return <>{children}</>;
};

export default AnalyticsRoute;